import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { czyObszarChroniony } from "./obszaryChronione";
import { czyKlawiszZrzutu, CZAS_ZNAKU_WODNEGO_MS } from "./klawiszeZrzutu";
import s from "./OchronaEkranu.module.css";

/**
 * [polecenie właściciela 2026-09-16] ZNAK WODNY + TWARDA BLOKADA WYDRUKU NA DANYCH WRAŻLIWYCH.
 *
 * PYTANIE WŁAŚCICIELA brzmiało: „czy możemy zrobić tak, że jak ktoś zechce zrobić screena aplikacji,
 * to aplikacja zmieni się w czarny kolor i nie pozwoli zrobić zrzutu ekranu (…) jak wykryjesz screena
 * z danymi, od razu na screenie zamieniaj dane na anonimowe".
 *
 * ODPOWIEDŹ, KTÓRA MUSI ZOSTAĆ ZAPISANA PRZY KODZIE: tego nie da się zrobić i nie należy udawać, że
 * się da. Zrzut ekranu wykonuje SYSTEM OPERACYJNY — strona nie dostaje o nim żadnego zdarzenia, nie
 * ma go jak wykryć, przerwać ani „zdążyć" podmienić danych. Nasłuch klawisza PrintScreen łapałby
 * jeden ze sposobów, a wycinanie fragmentu ekranu narzędziem systemowym, drugi monitor, telefon
 * skierowany w ekran czy nagranie pulpitu nie dotykają przeglądarki wcale. Blokada, która działa
 * w jednym przypadku na pięć, jest GORSZA niż jej brak: daje poczucie ochrony, której nie ma.
 *
 * ZAKRES PRZYJĘTY PRZEZ WŁAŚCICIELA („tak, tym zakresem") — dwie rzeczy, które działają naprawdę:
 *   1. ZNAK WODNY: imię, nazwisko, konto i czas, powtórzone po skosie przez cały ekran. Zrzut wychodzi
 *      PODPISANY, więc wyciek da się przypisać do osoby. To środek odstraszający i dowodowy — nie
 *      zapobiega zrobieniu zdjęcia, sprawia, że nie opłaca się go rozesłać.
 *
 *      🔴 [POPRAWKA, zgłoszenie właściciela 2026-09-17] „weź mi to wywal, to nie może tak być, że mam znak
 *      wody — to ma być tylko w momencie, jak wykryje klawisz print screen, inaczej nieee."
 *      Znak wodny stał NA STAŁE na sześciu obszarach i był widoczny przy zwykłej pracy: na liście wniosków
 *      HR, w kartotece, w e-teczce. To nie jest ochrona, to jest brudna szyba między człowiekiem a danymi,
 *      z którymi pracuje osiem godzin dziennie. TERAZ warstwa pojawia się WYŁĄCZNIE po wykryciu klawisza
 *      zrzutu i gaśnie sama. Co to realnie łapie, a czego nie — wypisane w `klawiszeZrzutu.js`; najkrócej:
 *      PrintScreen widzimy PO zrzucie (znak wejdzie na następny), a Win+Shift+S PRZED wycięciem obszaru.
 *   2. BLOKADA WYDRUKU I „DRUKUJ DO PDF": tutaj przeglądarka daje realne narzędzie (`@media print`),
 *      więc najprostsza droga do CZYSTEJ kopii dokumentu zostaje zamknięta. Zamiast treści na kartce
 *      wychodzi informacja, kto i kiedy próbował.
 *
 * Znak wodny nie przechwytuje kliknięć (`pointer-events: none`) i nie dotyka DANYCH — leży wyłącznie
 * na wierzchu obrazu. To ważne: warstwa ochronna, która psuje pracę, zostaje wyłączona po tygodniu.
 */
const OchronaEkranu = () => {
    const location = useLocation();
    const { user } = useAuth();

    const chroniony = czyObszarChroniony(location.pathname);

    /* Znak wodny NIE stoi na stałe — pokazuje się po wykryciu klawisza zrzutu i gaśnie sam.
       Powód i granice tego rozpoznania: `klawiszeZrzutu.js`. */
    const [znakWodny, setZnakWodny] = useState(false);
    const wygaszenie = useRef(null);

    const pokazZnakWodny = useCallback(() => {
        setZnakWodny(true);
        if (wygaszenie.current) clearTimeout(wygaszenie.current);
        wygaszenie.current = setTimeout(() => setZnakWodny(false), CZAS_ZNAKU_WODNEGO_MS);
    }, []);

    /* Podpis liczymy RAZ na wejście w obszar — i celowo z minutą, a nie z sekundą: znak wodny ma być
       czytelny na zrzucie, a nie migać co sekundę i rozpraszać przy pracy. */
    const podpis = useMemo(() => {
        if (!chroniony) return "";
        const osoba = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
        const konto = user?.email || (user?.id != null ? `konto #${user.id}` : "");
        const teraz = new Date();
        const czas = teraz.toLocaleString("pl-PL", {
            year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
        });
        return [osoba || "nieznane konto", konto, czas].filter(Boolean).join(" · ");
        // eslint-disable-next-line react-hooks/exhaustive-deps -- świadomie liczone przy wejściu w obszar
    }, [chroniony, user?.id, user?.email, user?.firstName, user?.lastName]);

    /* Blokada druku steruje ATRYBUTEM na <html>, a reguły `@media print` siedzą w arkuszu globalnym
       (`styles/ochronaEkranu.css`). Dlaczego nie styl wstrzykiwany z komponentu: reguła druku musi
       obowiązywać CAŁY dokument, także elementy portalowane do <body> (okna, szuflady, podglądy),
       a te są poza drzewem tego komponentu. */
    useEffect(() => {
        const korzen = document.documentElement;
        if (!chroniony) {
            korzen.removeAttribute("data-ochrona-ekranu");
            return undefined;
        }
        korzen.setAttribute("data-ochrona-ekranu", "1");
        return () => korzen.removeAttribute("data-ochrona-ekranu");
    }, [chroniony]);

    /* Ctrl+P / ⌘+P zatrzymujemy, zanim otworzy się okno druku — żeby człowiek dostał JASNY komunikat,
       a nie pustą kartkę bez wyjaśnienia. To wygoda, nie zabezpieczenie: druk z menu przeglądarki
       tego nie dotknie i właśnie dlatego prawdziwą blokadą jest reguła `@media print`. */
    useEffect(() => {
        if (!chroniony) return undefined;
        const naKlawisz = (e) => {
            if (!(e.ctrlKey || e.metaKey) || String(e.key).toLowerCase() !== "p") return;
            e.preventDefault();
            e.stopPropagation();
        };
        window.addEventListener("keydown", naKlawisz, true);
        return () => window.removeEventListener("keydown", naKlawisz, true);
    }, [chroniony]);

    /* Klawisz zrzutu: `keydown` ORAZ `keyup`, bo na Windowsie PrintScreen zwykle nie wywołuje `keydown`.
       Podwójne wywołanie nie szkodzi — drugie tylko przedłuża czas wyświetlenia. Nasłuch działa WYŁĄCZNIE
       w obszarach chronionych; poza nimi komponent nic nie montuje. */
    useEffect(() => {
        if (!chroniony) return undefined;
        const naKlawisz = (e) => { if (czyKlawiszZrzutu(e)) pokazZnakWodny(); };
        window.addEventListener("keydown", naKlawisz, true);
        window.addEventListener("keyup", naKlawisz, true);
        return () => {
            window.removeEventListener("keydown", naKlawisz, true);
            window.removeEventListener("keyup", naKlawisz, true);
        };
    }, [chroniony, pokazZnakWodny]);

    /* Wyjście z obszaru gasi warstwę i kasuje zegar — inaczej znak wodny przeżyłby przejście
       na zwykłą stronę, czyli dokładnie to, o co właściciel miał pretensję. */
    useEffect(() => {
        if (chroniony) return undefined;
        setZnakWodny(false);
        if (wygaszenie.current) clearTimeout(wygaszenie.current);
        return undefined;
    }, [chroniony]);

    useEffect(() => () => { if (wygaszenie.current) clearTimeout(wygaszenie.current); }, []);

    if (!chroniony) return null;

    return (
        <>
            {znakWodny && (
                <div className={s.znakWodny} aria-hidden="true" data-testid="znak-wodny">
                    {/* Pasy powtarzane po skosie: jeden napis dałoby się wyciąć kadrem. */}
                    {Array.from({ length: 14 }).map((_, i) => (
                        <div key={i} className={s.pas}>
                            {Array.from({ length: 6 }).map((__, j) => (
                                <span key={j} className={s.napis}>{podpis}</span>
                            ))}
                        </div>
                    ))}
                </div>
            )}

            {/* Widoczne WYŁĄCZNIE na wydruku (arkusz globalny) — to jest treść, która wyjdzie na kartce. */}
            {/* Zaczepieniem dla arkusza globalnego jest ATRYBUT, a nie klasa: nazwy klas z CSS Modules
                są mieszane przy budowaniu, więc reguła `@media print` w arkuszu globalnym nie miałaby
                jak w nią trafić. */}
            <div className={s.komunikatDruku} data-ochrona="komunikat-druku" data-testid="blokada-wydruku">
                <strong>Wydruk tej sekcji jest wyłączony.</strong>
                <span>Dane osobowe i dane poufne firmy nie są udostępniane w formie wydruku ani pliku PDF.</span>
                <span>{podpis}</span>
            </div>
        </>
    );
};

export default OchronaEkranu;
